import * as React from 'react';

import { cn } from 'lib/utils';

/**
 * Extraído documentando Screens/Mi Beneficio e Inicio B2C: el mismo
 * `<div className="w-N h-N rounded-{xl|full|lg} flex items-center
 * justify-center bg-...">` con un icono lucide dentro se repetía suelto más de
 * 10 veces entre las dos pantallas (MetricCardV2, RankingRowV2, returns strip,
 * TeamAdoptionV2, EducationalTipV2, ExternalPlanCTAV2, TransferInfoCard...) —
 * no existía como pieza propia, cada sitio lo volvía a escribir a mano. No es
 * una recreación de un componente real con ese nombre: es la generalización
 * del patrón visual repetido, para dejar de duplicar el div.
 */
export interface IconBadgeProps {
	icon: React.ComponentType<{ className?: string }>;
	/** sm=32px (listas/filas), md=36-40px (cards/metrics, por defecto), lg=48px (heros/CTAs). */
	size?: 'sm' | 'md' | 'lg';
	/** primary = bg-primary/10 + icono primary (el caso más común, "dato positivo/marca").
	 *  muted = bg-muted + icono muted-foreground (contexto, no protagonista — origen de un traspaso).
	 *  white = bg-surface-elevated + icono foreground (sobre fondos ya tintados, ej. tarjetas
	 *  bg-primary/5). En claro es blanco; en oscuro se levanta un escalón sobre la tarjeta. */
	tone?: 'primary' | 'muted' | 'white';
	shape?: 'rounded' | 'circle';
	className?: string;
}

const SIZE_CLASSES: Record<NonNullable<IconBadgeProps['size']>, string> = {
	sm: 'w-8 h-8',
	md: 'w-10 h-10',
	lg: 'w-12 h-12'
};

const ICON_SIZE_CLASSES: Record<NonNullable<IconBadgeProps['size']>, string> = {
	sm: 'w-3.5 h-3.5',
	md: 'w-5 h-5',
	lg: 'w-6 h-6'
};

const TONE_CLASSES: Record<NonNullable<IconBadgeProps['tone']>, string> = {
	// Sobre su propio `bg-primary/10` el icono daba 2,82:1, por debajo del 3:1 que pide un
	// elemento de interfaz. Con `--primary-text`, 6,60:1, y el oscuro no se mueve.
	primary: 'bg-primary/10 text-primary-text',
	muted: 'bg-muted text-muted-foreground',
	// `bg-white dark:bg-card` era un primitivo para el claro y un token para el oscuro. Lo que
	// esta variante quiere decir es «una superficie que se levanta sobre la tarjeta», y para eso
	// está `--surface-elevated`, que ya lo dice en los dos temas. En oscuro pasa de neutral-24 a
	// neutral-29, o sea deja de ser exactamente la tarjeta y se levanta un escalón, que es lo que
	// la variante prometía. El nombre `white` se conserva porque es API pública.
	white: 'bg-surface-elevated text-foreground'
};

export const IconBadge = ({
	icon: Icon,
	size = 'md',
	tone = 'primary',
	shape = 'rounded',
	className
}: IconBadgeProps) => (
	<div
		className={cn(
			'flex items-center justify-center shrink-0',
			shape === 'circle' ? 'rounded-full' : 'rounded-xl',
			SIZE_CLASSES[size],
			TONE_CLASSES[tone],
			className
		)}
	>
		<Icon className={ICON_SIZE_CLASSES[size]} />
	</div>
);
